import { useState, useRef, useEffect } from "react";
import axios from "axios";

const CalendarSection = ({
  activeTab,
  setActiveTab,
  selectedField,
  setSelectedField,
  selectedDates,
  setSelectedDates,
  currentMonth,
  setCurrentMonth,
  currentYear,
  setCurrentYear,
  formData,
  setFormData,
  fetchBookedEvents,
  bookedDates,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [fields, setFields] = useState([]);
  const dropdownRef = useRef(null);

  // Fetch fields
  useEffect(() => {
    const fetchFields = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/fields-rental/all-fields`
        );
        setFields(res.data.rentals);
      } catch (err) {
        console.error("Error fetching fields:", err);
      }
    };

    fetchFields();
  }, []);

  // Fetch booked events on field select
  useEffect(() => {
    if (!formData.field) return;
    fetchBookedEvents();
  }, [formData.field]);

  // Dropdown close handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleFieldSelect = (field) => {
    setSelectedField(field.fieldName);
    setFormData((prev) => ({
      ...prev,
      field: field._id,
    }));
    setSelectedDates([]);
    setShowDropdown(false);
  };

  const getDaysInMonth = (month, year) =>
    new Date(year, month + 1, 0).getDate();

  const getFirstDayOfMonth = (month, year) => new Date(year, month, 1).getDay();

  const handleDateSelect = (day) => {
    if (day > 0 && day <= getDaysInMonth(currentMonth, currentYear)) {
      const dateStr = `${currentYear}-${(currentMonth + 1)
        .toString()
        .padStart(2, "0")}-${day.toString().padStart(2, "0")}`;

      if (bookedDates.includes(dateStr)) return;

      if (activeTab === "single") {
        setSelectedDates(selectedDates.includes(dateStr) ? [] : [dateStr]);
      } else {
        if (selectedDates.includes(dateStr)) {
          setSelectedDates(selectedDates.filter((d) => d !== dateStr));
        } else if (selectedDates.length < 2) {
          setSelectedDates([...selectedDates, dateStr].sort());
        } else {
          setSelectedDates([...selectedDates.slice(1), dateStr].sort());
        }
      }
    }
  };

  const handleMonthChange = (increment) => {
    let newMonth = currentMonth + increment;
    let newYear = currentYear;
    const now = new Date();

    if (newMonth > 11) {
      newMonth = 0;
      newYear++;
    } else if (newMonth < 0) {
      if (
        currentYear === now.getFullYear() &&
        currentMonth === now.getMonth()
      ) {
        return;
      }
      newMonth = 11;
      newYear--;
    } else if (newYear === now.getFullYear() && newMonth < now.getMonth()) {
      return;
    }

    setCurrentMonth(newMonth);
    setCurrentYear(newYear);
    setSelectedDates([]);
  };

  const daysInMonth = getDaysInMonth(currentMonth, currentYear);
  const firstDayOfMonth = getFirstDayOfMonth(currentMonth, currentYear);

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const calendarDays = [];
  const daysFromPrevMonth = firstDayOfMonth;
  const prevMonthDays = getDaysInMonth(currentMonth - 1, currentYear);

  for (let i = 0; i < daysFromPrevMonth; i++) {
    calendarDays.push({
      day: prevMonthDays - daysFromPrevMonth + i + 1,
      isCurrentMonth: false,
    });
  }

  for (let i = 1; i <= daysInMonth; i++) {
    calendarDays.push({
      day: i,
      isCurrentMonth: true,
    });
  }

  const totalCells = 42;
  for (let i = 1; i <= totalCells - calendarDays.length; i++) {
    calendarDays.push({
      day: i,
      isCurrentMonth: false,
    });
  }

  return (
    <div className="w-full lg:w-2/5 flex flex-col">
      <div className="bg-gradient-to-br from-[#24173a] to-[#1a0d2d] p-6 rounded-2xl shadow-xl h-full flex flex-col">
        {/* Tabs */}
        <div className="flex mb-6 bg-[#1E1235] rounded-xl p-1 border border-gray-700/30">
          <button
            type="button"
            className={`flex-1 py-3 px-4 rounded-xl transition-all text-sm font-medium ${
              activeTab === "single"
                ? "bg-gradient-to-r from-red-600 to-red-700 text-white shadow"
                : "text-gray-300 hover:text-white"
            }`}
            onClick={() => {
              setActiveTab("single");
              setSelectedDates([]);
            }}
          >
            Single Match
          </button>
          <button
            type="button"
            className={`flex-1 py-3 px-4 rounded-xl transition-all text-sm font-medium ${
              activeTab === "tournament"
                ? "bg-gradient-to-r from-purple-600 to-purple-700 text-white shadow"
                : "text-gray-300 hover:text-white"
            }`}
            onClick={() => {
              setActiveTab("tournament");
              setSelectedDates([]);
            }}
          >
            Tournament
          </button>
        </div>

        <h3 className="text-2xl font-bold mb-2 text-white">
          Select Date & Field
        </h3>
        <p className="text-gray-300 mb-6 text-sm">
          First choose your field, then pick a date
        </p>

        {/* Field Dropdown */}
        <div className="relative mb-6" ref={dropdownRef}>
          <button
            type="button"
            onClick={() => setShowDropdown(!showDropdown)}
            className="w-full flex justify-between items-center px-4 py-3 rounded-xl bg-[#1E1235] text-white border border-gray-700/30"
          >
            {selectedField || "Select a Field"}
            <svg
              className={`w-4 h-4 ml-2 transition-transform ${
                showDropdown ? "rotate-180" : ""
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
          {showDropdown && (
            <div className="absolute w-full mt-2 bg-[#1E1235] border border-gray-700/30 rounded-xl shadow-lg z-10">
              {fields.map((field) => (
                <button
                  key={field._id}
                  type="button"
                  onClick={() => handleFieldSelect(field)}
                  className="flex items-center w-full px-4 py-3 text-left text-white hover:bg-[#2A1B45] rounded-xl"
                >
                  <img
                    src={field.imageUrl}
                    alt={field.fieldName}
                    className="w-6 h-6 rounded mr-3 object-cover"
                  />
                  {field.fieldName}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Calendar */}
        <div className="flex-1 bg-gradient-to-b from-[#1E1235] to-[#24173a] rounded-xl p-4 flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <button
              type="button"
              onClick={() => handleMonthChange(-1)}
              className="p-2 rounded-lg bg-[#2A1B45] hover:bg-[#352355] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              disabled={
                currentYear === new Date().getFullYear() &&
                currentMonth === new Date().getMonth()
              }
            >
              ◀
            </button>
            <h4 className="text-lg font-semibold text-white">
              {monthNames[currentMonth]} {currentYear}
            </h4>
            <button
              type="button"
              onClick={() => handleMonthChange(1)}
              className="p-2 rounded-lg bg-[#2A1B45] hover:bg-[#352355] transition-colors"
            >
              ▶
            </button>
          </div>

          {/* Week Days */}
          <div className="grid grid-cols-7 gap-2 mb-2">
            {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
              <div
                key={day}
                className="text-center text-xs text-purple-300 font-medium"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Days */}
          <div className="grid grid-cols-7 gap-2">
            {calendarDays.map(({ day, isCurrentMonth }, index) => {
              const dateStr = isCurrentMonth
                ? `${currentYear}-${(currentMonth + 1)
                    .toString()
                    .padStart(2, "0")}-${day.toString().padStart(2, "0")}`
                : null;
              const isSelected = dateStr && selectedDates.includes(dateStr);
              const isBooked = dateStr && bookedDates.includes(dateStr);

              return (
                <div
                  key={index}
                  onClick={() =>
                    isCurrentMonth && !isBooked && handleDateSelect(day)
                  }
                  className={`h-8 flex items-center justify-center rounded-lg text-sm
                    ${!isCurrentMonth ? "invisible" : ""}
                    ${
                      isSelected
                        ? "bg-gradient-to-r from-red-500 to-purple-600 text-white shadow"
                        : ""
                    }
                    ${
                      !isSelected && !isBooked && isCurrentMonth
                        ? "bg-[#352355] text-white hover:bg-[#3f2a66] cursor-pointer"
                        : ""
                    }
                    ${
                      isBooked
                        ? "bg-red-900/70 text-gray-300 cursor-not-allowed"
                        : ""
                    }
                  `}
                >
                  {day}
                </div>
              );
            })}
          </div>

          {/* Selected Dates */}
          {selectedDates.length > 0 && (
            <div className="p-3 bg-[#2A1B45] rounded-lg">
              <h5 className="text-sm font-medium text-white mb-2">
                {activeTab === "single" ? "Selected Date:" : "Selected Dates:"}
              </h5>
              <div className="flex flex-wrap gap-2">
                {selectedDates.sort().map((date) => (
                  <span
                    key={date}
                    className="px-2 py-1 bg-purple-700/30 text-xs rounded-md text-white"
                  >
                    {/* FIXED DATE FORMAT TO AVOID TIMEZONE SHIFT */}
                    {(() => {
                      const [y, m, d] = date.split("-");
                      const months = [
                        "Jan","Feb","Mar","Apr","May","Jun",
                        "Jul","Aug","Sep","Oct","Nov","Dec"
                      ];
                      return `${months[m - 1]} ${parseInt(d)}`;
                    })()}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Legend */}
          <div className="mt-4 pt-4 border-t border-gray-700/50">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-gradient-to-r from-red-500 to-purple-600 mr-2"></div>
                <span className="text-xs text-gray-300">Selected dates</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-[#352355] mr-2"></div>
                <span className="text-xs text-gray-300">Available</span>
              </div>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 rounded-full bg-red-900/70 mr-2"></div>
              <span className="text-xs text-gray-300">Booked</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarSection;








// import { useState, useRef, useEffect } from "react";
// import axios from "axios";

// const CalendarSection = ({
//   activeTab,
//   setActiveTab,
//   selectedField,
//   setSelectedField,
//   selectedDates,
//   setSelectedDates,
//   currentMonth,
//   setCurrentMonth,
//   currentYear,
//   setCurrentYear,
//   formData,
//   setFormData,
//   fetchBookedEvents,
//   bookedDates,
// }) => {
//   const [showDropdown, setShowDropdown] = useState(false);
//   const [fields, setFields] = useState([]); // ✅ store fields from API
//   const dropdownRef = useRef(null);

//   // ✅ Fetch fields from API
//   useEffect(() => {
//     const fetchFields = async () => {
//       try {
//         const res = await axios.get(
//           `${process.env.REACT_APP_API_URL}/fields-rental/all-fields`
//         );
//         setFields(res.data.rentals);
//       } catch (err) {
//         console.error("Error fetching fields:", err);
//       }
//     };

//     fetchFields();
//   }, []);

//   // ✅ Fetch booked events for the selected field
//   useEffect(() => {
//     if (!formData.field) return;

//     fetchBookedEvents();
//   }, [formData.field]);

//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
//         setShowDropdown(false);
//       }
//     };
//     document.addEventListener("mousedown", handleClickOutside);
//     return () => {
//       document.removeEventListener("mousedown", handleClickOutside);
//     };
//   }, []);

//   const handleFieldSelect = (field) => {
//     setSelectedField(field.fieldName);
//     setFormData((prev) => ({
//       ...prev,
//       field: field._id,
//     }));
//     setSelectedDates([]); // reset selection when field changes
//     setShowDropdown(false);
//   };

//   const getDaysInMonth = (month, year) =>
//     new Date(year, month + 1, 0).getDate();

//   const getFirstDayOfMonth = (month, year) => new Date(year, month, 1).getDay();

//   const handleDateSelect = (day) => {
//     if (day > 0 && day <= getDaysInMonth(currentMonth, currentYear)) {
//       const dateStr = `${currentYear}-${(currentMonth + 1)
//         .toString()
//         .padStart(2, "0")}-${day.toString().padStart(2, "0")}`;

//       // ❌ prevent selecting booked dates
//       if (bookedDates.includes(dateStr)) return;

//       if (activeTab === "single") {
//         setSelectedDates(selectedDates.includes(dateStr) ? [] : [dateStr]);
//       } else {
//         if (selectedDates.includes(dateStr)) {
//           setSelectedDates(selectedDates.filter((d) => d !== dateStr));
//         } else if (selectedDates.length < 2) {
//           setSelectedDates([...selectedDates, dateStr].sort());
//         } else {
//           setSelectedDates([...selectedDates.slice(1), dateStr].sort());
//         }
//       }
//     }
//   };

//   const handleMonthChange = (increment) => {
//     let newMonth = currentMonth + increment;
//     let newYear = currentYear;
//     const now = new Date();

//     if (newMonth > 11) {
//       newMonth = 0;
//       newYear++;
//     } else if (newMonth < 0) {
//       if (
//         currentYear === now.getFullYear() &&
//         currentMonth === now.getMonth()
//       ) {
//         return;
//       }
//       newMonth = 11;
//       newYear--;
//     } else if (newYear === now.getFullYear() && newMonth < now.getMonth()) {
//       return;
//     }

//     setCurrentMonth(newMonth);
//     setCurrentYear(newYear);
//     setSelectedDates([]);
//   };

//   const daysInMonth = getDaysInMonth(currentMonth, currentYear);
//   const firstDayOfMonth = getFirstDayOfMonth(currentMonth, currentYear);

//   const monthNames = [
//     "January",
//     "February",
//     "March",
//     "April",
//     "May",
//     "June",
//     "July",
//     "August",
//     "September",
//     "October",
//     "November",
//     "December",
//   ];

//   const calendarDays = [];
//   const daysFromPrevMonth = firstDayOfMonth;
//   const prevMonthDays = getDaysInMonth(currentMonth - 1, currentYear);

//   for (let i = 0; i < daysFromPrevMonth; i++) {
//     calendarDays.push({
//       day: prevMonthDays - daysFromPrevMonth + i + 1,
//       isCurrentMonth: false,
//     });
//   }

//   for (let i = 1; i <= daysInMonth; i++) {
//     calendarDays.push({
//       day: i,
//       isCurrentMonth: true,
//     });
//   }

//   const totalCells = 42;
//   for (let i = 1; i <= totalCells - calendarDays.length; i++) {
//     calendarDays.push({
//       day: i,
//       isCurrentMonth: false,
//     });
//   }

//   return (
//     <div className="w-full lg:w-2/5 flex flex-col">
//       <div className="bg-gradient-to-br from-[#24173a] to-[#1a0d2d] p-6 rounded-2xl shadow-xl h-full flex flex-col ">
//         {/* Tabs */}
//         <div className="flex mb-6 bg-[#1E1235] rounded-xl p-1 border border-gray-700/30">
//           <button
//             type="button"
//             className={`flex-1 py-3 px-4 rounded-xl transition-all text-sm font-medium ${
//               activeTab === "single"
//                 ? "bg-gradient-to-r from-red-600 to-red-700 text-white shadow"
//                 : "text-gray-300 hover:text-white"
//             }`}
//             onClick={() => {
//               setActiveTab("single");
//               setSelectedDates([]);
//             }}
//           >
//             Single Match
//           </button>
//           <button
//             type="button"
//             className={`flex-1 py-3 px-4 rounded-xl transition-all text-sm font-medium ${
//               activeTab === "tournament"
//                 ? "bg-gradient-to-r from-purple-600 to-purple-700 text-white shadow"
//                 : "text-gray-300 hover:text-white"
//             }`}
//             onClick={() => {
//               setActiveTab("tournament");
//               setSelectedDates([]);
//             }}
//           >
//             Tournament
//           </button>
//         </div>

//         <h3 className="text-2xl font-bold mb-2 text-white">
//           Select Date & Field
//         </h3>
//         <p className="text-gray-300 mb-6 text-sm">
//           First choose your field, then pick a date
//         </p>

//         {/* Field Dropdown */}
//         <div className="relative mb-6" ref={dropdownRef}>
//           <button
//             type="button"
//             onClick={() => setShowDropdown(!showDropdown)}
//             className="w-full flex justify-between items-center px-4 py-3 rounded-xl bg-[#1E1235] text-white border border-gray-700/30"
//           >
//             {selectedField || "Select a Field"}
//             <svg
//               className={`w-4 h-4 ml-2 transition-transform ${
//                 showDropdown ? "rotate-180" : ""
//               }`}
//               fill="none"
//               stroke="currentColor"
//               viewBox="0 0 24 24"
//             >
//               <path
//                 strokeLinecap="round"
//                 strokeLinejoin="round"
//                 strokeWidth={2}
//                 d="M19 9l-7 7-7-7"
//               />
//             </svg>
//           </button>
//           {showDropdown && (
//             <div className="absolute w-full mt-2 bg-[#1E1235] border border-gray-700/30 rounded-xl shadow-lg z-10">
//               {fields.map((field) => (
//                 <button
//                   key={field._id}
//                   type="button"
//                   onClick={() => handleFieldSelect(field)}
//                   className="flex items-center w-full px-4 py-3 text-left text-white hover:bg-[#2A1B45] rounded-xl"
//                 >
//                   <img
//                     src={field.imageUrl}
//                     alt={field.fieldName}
//                     className="w-6 h-6 rounded mr-3 object-cover"
//                   />
//                   {field.fieldName}
//                 </button>
//               ))}
//             </div>
//           )}
//         </div>

//         {/* Calendar */}
//         <div className="flex-1 bg-gradient-to-b from-[#1E1235] to-[#24173a] rounded-xl p-4 flex flex-col">
//           <div className="flex items-center justify-between mb-4">
//             <button
//               type="button"
//               onClick={() => handleMonthChange(-1)}
//               className="p-2 rounded-lg bg-[#2A1B45] hover:bg-[#352355] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
//               disabled={
//                 currentYear === new Date().getFullYear() &&
//                 currentMonth === new Date().getMonth()
//               }
//             >
//               ◀
//             </button>
//             <h4 className="text-lg font-semibold text-white">
//               {monthNames[currentMonth]} {currentYear}
//             </h4>
//             <button
//               type="button"
//               onClick={() => handleMonthChange(1)}
//               className="p-2 rounded-lg bg-[#2A1B45] hover:bg-[#352355] transition-colors"
//             >
//               ▶
//             </button>
//           </div>

//           {/* Week Days */}
//           <div className="grid grid-cols-7 gap-2 mb-2">
//             {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
//               <div
//                 key={day}
//                 className="text-center text-xs text-purple-300 font-medium"
//               >
//                 {day}
//               </div>
//             ))}
//           </div>

//           {/* Days */}
//           <div className="grid grid-cols-7 gap-2">
//             {calendarDays.map(({ day, isCurrentMonth }, index) => {
//               const dateStr = isCurrentMonth
//                 ? `${currentYear}-${(currentMonth + 1)
//                     .toString()
//                     .padStart(2, "0")}-${day.toString().padStart(2, "0")}`
//                 : null;
//               const isSelected = dateStr && selectedDates.includes(dateStr);
//               const isBooked = dateStr && bookedDates.includes(dateStr);

//               return (
//                 <div
//                   key={index}
//                   onClick={() =>
//                     isCurrentMonth && !isBooked && handleDateSelect(day)
//                   }
//                   className={`h-8 flex items-center justify-center rounded-lg text-sm
//                     ${!isCurrentMonth ? "invisible" : ""}
//                     ${
//                       isSelected
//                         ? "bg-gradient-to-r from-red-500 to-purple-600 text-white shadow"
//                         : ""
//                     }
//                     ${
//                       !isSelected && !isBooked && isCurrentMonth
//                         ? "bg-[#352355] text-white hover:bg-[#3f2a66] cursor-pointer"
//                         : ""
//                     }
//                     ${
//                       isBooked
//                         ? "bg-red-900/70 text-gray-300 cursor-not-allowed"
//                         : ""
//                     }
//                   `}
//                 >
//                   {day}
//                 </div>
//               );
//             })}
//           </div>

//           {/* Selected Dates */}
//           {selectedDates.length > 0 && (
//             <div className="p-3 bg-[#2A1B45] rounded-lg">
//               <h5 className="text-sm font-medium text-white mb-2">
//                 {activeTab === "single" ? "Selected Date:" : "Selected Dates:"}
//               </h5>
//               <div className="flex flex-wrap gap-2">
//                 {selectedDates.sort().map((date) => (
//                   <span
//                     key={date}
//                     className="px-2 py-1 bg-purple-700/30 text-xs rounded-md text-white"
//                   >
//                     {new Date(date).toLocaleDateString("en-US", {
//                       month: "short",
//                       day: "numeric",
//                     })}
//                   </span>
//                 ))}
//               </div>
//             </div>
//           )}

//           {/* Legend */}
//           <div className="mt-4 pt-4 border-t border-gray-700/50">
//             <div className="flex items-center justify-between mb-2">
//               <div className="flex items-center">
//                 <div className="w-3 h-3 rounded-full bg-gradient-to-r from-red-500 to-purple-600 mr-2"></div>
//                 <span className="text-xs text-gray-300">Selected dates</span>
//               </div>
//               <div className="flex items-center">
//                 <div className="w-3 h-3 rounded-full bg-[#352355] mr-2"></div>
//                 <span className="text-xs text-gray-300">Available</span>
//               </div>
//             </div>
//             <div className="flex items-center">
//               <div className="w-3 h-3 rounded-full bg-red-900/70 mr-2"></div>
//               <span className="text-xs text-gray-300">Booked</span>
//             </div>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default CalendarSection;
